import { useRef, useState, useEffect } from 'react';

import { Box } from '../../common/ui/Box';

interface ResizableDevtoolsPanelProps {
    children: React.ReactNode;
    onClose: () => void;
    initialHeight?: number;
    minHeight?: number;
    maxHeight?: number;
}

export const ResizableDevtoolsPanel = (props: ResizableDevtoolsPanelProps) => {
    const { children, onClose, initialHeight = 400, minHeight = 200, maxHeight = window.innerHeight * 0.9 } = props;
    const [height, setHeight] = useState(initialHeight);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const newHeight = window.innerHeight - e.clientY;
            const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
            setHeight(clampedHeight);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, minHeight, maxHeight]);

    return (
        <Box
            ref={containerRef}
            position="fixed"
            left={0}
            right={0}
            bottom={0}
            zIndex={9999}
            backgroundColor="white"
            style={{
                height: `${height}px`,
                boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
            }}
        >
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                height="8px"
                style={{
                    cursor: 'ns-resize',
                    backgroundColor: isDragging ? '#0066cc' : 'transparent',
                    transition: 'background-color 0.2s',
                }}
                onMouseDown={() => setIsDragging(true)}
                onMouseEnter={(e) => {
                    if (!isDragging) {
                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isDragging) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }
                }}
            />
            <Box
                position="absolute"
                top="8px"
                right="8px"
                zIndex={10000}
                style={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 700,
                    userSelect: 'none',
                }}
                onClick={onClose}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e0e0e0';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                }}
            >
                ✕
            </Box>
            <Box height="100%" style={{ overflow: 'auto' }}>
                {children}
            </Box>
        </Box>
    );
};
